import { createContext, useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import omit from 'lodash/omit'
import vitAPI from '../api'
import Modal from '../components/Modal/Modal'
import LoadingScreen from '../components/LoadingScreen/LoadingScreen'

import { formatCartPricing } from '@booking/vit/dist/utilities'
import { formatModifyServicesResponse } from '../utilities/formatModifyServicesResponse'

const customErrors = {
  LOGIN_REQUIRED_001: `Our system indicates that there is an account associated with your email address, and as a result, you are unable to continue without logging in. Please log in using your existing account details to proceed.`,
}

const CartContext = createContext(null)

export const CartContextProvider = ({ children }) => {
  const [cartState, setCartState] = useState({
    loading: false,
    data: null,
    error: null,
    errorModalIsOpen: false,
  })
  const navigate = useNavigate()

  /**
   * @param {Object} arguments
   * @param {Object} arguments.createParams
   * @param {String} [arguments.itineraryId]
   * @param {String} [arguments.nextRoute]
   * */
  const cancelRebook = ({ createParams, itineraryId, onSuccess }) => {
    setCartState({
      ...cartState,
      loading: true,
    })

    const reservationParams = {
      ...createParams,
      itineraryInfo: omit(createParams?.itineraryInfo, ['cancellationItems']),
    }
    const cancellationParams = {
      itineraryInfo: omit(createParams?.itineraryInfo, ['reservationItems']),
    }

    return vitAPI
      .postCart(reservationParams)
      .then((res) => {
        const cartId = res?.data?.['hydra:member']?.[0]?.id
        return vitAPI.postReserveCart({ cartId, itineraryId })
      })
      .then(() => vitAPI.postCart(cancellationParams))
      .then((res) => {
        const cartId = res?.data?.['hydra:member']?.[0]?.id
        return vitAPI.postReserveCart({ cartId, itineraryId })
      })
      .then((res) => {
        const itinerary = res?.data?.['hydra:member']?.[0]
        if (itinerary) {
          setCartState({
            loading: false,
            data: {
              ...itinerary,
              pricing: formatCartPricing(itinerary),
            },
            error: null,
            errorModalIsOpen: false,
          })
          if (onSuccess) {
            onSuccess()
          }
        } else {
          throw new Error('unable to create cart')
        }
      })
      .catch((err) => {
        setCartState({
          loading: false,
          ...cartState,
          error:
            err?.response?.data?.['hydra:description'] ||
            err?.message ||
            'Something went wrong',
          errorModalIsOpen: true,
        })
      })
  }

  /**
   * @param {Object} arguments
   * @param {Object} arguments.createParams
   * @param {String} [arguments.itineraryId]
   * @param {String} [arguments.nextRoute]
   * */
  const addToItinerary = ({ createParams, itineraryId, onSuccess }) => {
    setCartState({
      ...cartState,
      loading: true,
    })

    return vitAPI
      .postCart(createParams)
      .then((res) => {
        const cartId = res?.data?.['hydra:member']?.[0]?.id
        return vitAPI.postReserveCart({ cartId, itineraryId })
      })
      .then((res) => {
        const itinerary = res?.data?.['hydra:member']?.[0]
        if (itinerary) {
          setCartState({
            loading: false,
            data: {
              ...itinerary,
              pricing: formatCartPricing(itinerary),
            },
            error: null,
            errorModalIsOpen: false,
          })
          if (onSuccess) {
            onSuccess()
          }
        } else {
          throw new Error('unable to create cart')
        }
      })
      .catch((err) => {
        setCartState({
          loading: false,
          ...cartState,
          error:
            err?.response?.data?.['hydra:description'] ||
            err?.message ||
            'Something went wrong',
          errorModalIsOpen: true,
        })
      })
  }

  /**
   * @param {Object} arguments
   * @param {Object} arguments.createParams
   * @param {Object} arguments.hotel
   * @param {String} [arguments.nextRoute]
   * @param {Function} arguments.onSuccess
   * */
  const modifyServices = ({ createParams, hotel, nextRoute, onSuccess }) => {
    setCartState({
      ...cartState,
      loading: true,
    })
    let reserveCartAfterCreate = false
    return vitAPI
      .postCart(createParams)
      .then((res) => {
        const cart = res?.data?.['hydra:member']?.[0]
        if (cart) {
          if (cart?.itineraryPricing?.[0]?.cost?.amountAfterTax > 0) {
            return Promise.resolve(res)
          } else {
            reserveCartAfterCreate = true
            const cartId = res?.data?.['hydra:member']?.[0]?.id
            return vitAPI.postReserveCart({
              cartId,
              itineraryId: createParams?.id,
            })
          }
        } else {
          throw new Error('unable to modify itinerary')
        }
      })
      .then((res) => {
        const itinerary = res?.data?.['hydra:member']?.[0]
        if (itinerary) {
          const formattedItinerary = formatModifyServicesResponse(
            itinerary,
            hotel
          )
          setCartState({
            loading: false,
            data: {
              ...formattedItinerary,
              pricing: formatCartPricing(
                formattedItinerary,
                true,
                'modificationItems'
              ),
            },
            error: null,
            errorModalIsOpen: false,
          })
          if (reserveCartAfterCreate) {
            onSuccess()
          } else {
            navigate(nextRoute)
          }
        } else {
          throw new Error('unable to modify itinerary')
        }
      })
      .catch((err) => {
        setCartState({
          loading: false,
          ...cartState,
          error:
            err?.response?.data?.['hydra:description'] ||
            err?.message ||
            'Something went wrong',
          errorModalIsOpen: true,
        })
      })
  }

  /**
   * @param {Object} arguments
   * @param {Object} arguments.createParams
   * @param {Function} arguments.onSuccess
   * */
  const modifyItinerary = ({ createParams, onSuccess }) => {
    setCartState({
      ...cartState,
      loading: true,
    })
    return vitAPI
      .postCart(createParams)
      .then((res) => {
        const cart = res?.data?.['hydra:member']?.[0]
        if (cart) {
          const cartId = cart?.id
          return vitAPI.postReserveCart({
            cartId,
            itineraryId: createParams?.id,
          })
        } else {
          throw new Error('unable to modify itinerary')
        }
      })
      .then((res) => {
        const itinerary = res?.data?.['hydra:member']?.[0]
        if (itinerary) {
          setCartState({
            loading: false,
            data: {
              ...itinerary,
              pricing: formatCartPricing(itinerary, true, 'modificationItems'),
            },
            error: null,
            errorModalIsOpen: false,
          })
          onSuccess()
        } else {
          throw new Error('unable to modify itinerary')
        }
      })
      .catch((err) => {
        setCartState({
          loading: false,
          ...cartState,
          error:
            err?.response?.data?.['hydra:description'] ||
            err?.message ||
            'Something went wrong',
          errorModalIsOpen: true,
        })
      })
  }

  /**
   * @param {Object} arguments
   * @param {String} [arguments.nextRoute]
   * @param {Object} arguments.createParams
   * */
  const createCart = ({ createParams, nextRoute }) => {
    setCartState({
      ...cartState,
      loading: true,
    })

    return vitAPI
      .postCart(createParams)
      .then((res) => {
        const cart = res?.data?.['hydra:member']?.[0]
        if (cart) {
          const data = {
            ...cart,
            pricing: formatCartPricing(cart),
          }
          setCartState({
            loading: false,
            data,
            error: null,
            errorModalIsOpen: false,
          })
          if (nextRoute) {
            navigate(nextRoute)
          }
          return data
        } else {
          throw new Error('unable to create cart')
        }
      })
      .catch((err) => {
        const errorCode =
          err?.response?.data?.violations?.[0]?.propertyPath || null

        const customError =
          errorCode && customErrors[errorCode] ? customErrors[errorCode] : null

        setCartState({
          loading: false,
          ...cartState,
          error:
            customError ||
            err?.response?.data?.['hydra:description'] ||
            err?.message ||
            'Something went wrong',
          errorModalIsOpen: true,
        })
      })
  }

  const closeCartErrorModal = () => {
    setCartState({
      ...cartState,
      errorModalIsOpen: false,
    })
  }
  const resetCart = () => {
    setCartState({
      loading: false,
      data: null,
      error: null,
      errorModalIsOpen: false,
    })
  }

  return (
    <CartContext.Provider
      value={{
        addToItinerary,
        cancelRebook,
        cartState,
        createCart,
        closeCartErrorModal,
        modifyItinerary,
        modifyServices,
        resetCart,
      }}
    >
      <>
        {children}
        <Modal
          isOpen={cartState.loading}
          overlayClassName="bg-white bg-opacity-50"
        >
          <LoadingScreen />
        </Modal>
        {cartState?.errorModalIsOpen && (
          <Modal
            isOpen={cartState?.errorModalIsOpen}
            onRequestClose={closeCartErrorModal}
          >
            <div className="bg-white p-10">
              <p className="mb-8">{cartState.error}</p>
              <button className="btn btn-primary" onClick={closeCartErrorModal}>
                Dismiss
              </button>
            </div>
          </Modal>
        )}
      </>
    </CartContext.Provider>
  )
}

const useCartContext = () => useContext(CartContext)

export default useCartContext
